import React, { useState } from "react";
import { useEntitlement } from "../../hooks/useEntitlements";
import usePageTitle from "../../hooks/usePageTitle";
import { captureScreenshot } from "../../utils/captureScreenshot";
import { BugIcon } from "../layout/v2/ShellIcons";
import { SubmitTicketDialog } from "./SubmitTicketDialog";

interface ReportIssueButtonProps {
  /** Hides the text label, leaving an icon-only button (narrow layouts). */
  compact?: boolean;
  /**
   * Label of the currently active rail/sidebar section (e.g. "Needs
   * Assessment"), as already computed by AppShell for the sidebar/subnav.
   * Falls back to usePageTitle() when unset, since that hook's route table
   * doesn't cover every sub-page (e.g. /needs-assessment/templates).
   */
  pageLabel?: string;
}

export const ReportIssueButton: React.FC<ReportIssueButtonProps> = ({
  compact = false,
  pageLabel,
}) => {
  const canCreate = useEntitlement("SupportTicket.Create");
  const routeTitle = usePageTitle();
  const pageTitle = pageLabel || routeTitle;
  const [open, setOpen] = useState(false);
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [capturing, setCapturing] = useState(false);

  if (!canCreate) return null;

  const handleClick = async () => {
    setCapturing(true);
    const shot = await captureScreenshot();
    setCapturing(false);
    setScreenshot(shot);
    setOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => void handleClick()}
        disabled={capturing}
        aria-label="Report an issue"
        title="Report an issue"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          height: 32,
          padding: compact ? 0 : "0 10px",
          width: compact ? 32 : undefined,
          justifyContent: compact ? "center" : undefined,
          borderRadius: "var(--r-sm)",
          border: "1px solid var(--line)",
          background: "transparent",
          color: "var(--ink-2)",
          fontSize: 13,
          cursor: capturing ? "wait" : "pointer",
          flexShrink: 0,
          transition: "border-color 100ms, color 100ms",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.borderColor =
            "var(--line-strong)";
          (e.currentTarget as HTMLButtonElement).style.color = "var(--ink-1)";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.borderColor =
            "var(--line)";
          (e.currentTarget as HTMLButtonElement).style.color = "var(--ink-2)";
        }}
      >
        <BugIcon size={14} />
        {!compact && <span>Report Issue</span>}
      </button>

      <SubmitTicketDialog
        open={open}
        onClose={() => setOpen(false)}
        initialSubject={`${pageTitle} issue: `}
        initialScreenshot={screenshot}
      />
    </>
  );
};

export default ReportIssueButton;
